import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AvatarText } from 'components/Shared/AvatarText';
import { useHistory } from 'hooks/generic/useHistory';
import { useIsImportBlockchainNft } from 'hooks/nft/useIsImportBlockchainNft';
import { NftAvatarBlockSkeleton } from './Skeleton';
import { useAppSelector } from 'appRedux/hooks';
import { CheckIcon } from 'icons/colored/Check';
import placeholderImage from 'assets/images/user-image.png';
import { INft, TSizeType } from 'interfaces';

export type NftAvatarBlockProps = {
  type?: 'owner' | 'creator' | 'collection';
  withLink?: boolean;
  size?: TSizeType;
  hideDescription?: boolean;
  showAvatar?: boolean;
  reverseText?: boolean;
  useCurrentNft?: boolean;
  nft?: INft;
  className?: string;
};
export const NftAvatarBlock: FC<NftAvatarBlockProps> = ({
  type = 'owner',
  withLink = false,
  size = 'medium',
  hideDescription = false,
  showAvatar,
  reverseText,
  useCurrentNft = true,
  nft,
  className
}) => {
  const { t } = useTranslation();
  const { push } = useHistory();
  const {
    current: { loading, data: currentNftData }
  } = useAppSelector(({ nfts }) => nfts);
  const isImportBlockchainNft = useIsImportBlockchainNft();

  const data = useMemo(() => {
    const nftData = useCurrentNft ? currentNftData : nft;
    if (!nftData) {
      return null;
    }
    switch (type) {
      case 'owner':
        return {
          description: t('artist.owner'),
          link: `/artists/${nftData.owned_by?.user_name}`,
          name: nftData.owned_by?.user_name,
          avatar: nftData.owned_by.logo_url || placeholderImage,
          verified: nftData.owned_by.verified
        };
      case 'creator':
        return {
          description: t('artist.creator'),
          link: `/artists/${nftData.created_by?.user_name}`,
          name:
            nftData.created_by?.user_name || Math.floor(Math.random() * 10000),
          avatar: nftData.created_by.logo_url || placeholderImage,
          verified: nftData.created_by.verified
        };
      case 'collection':
        return {
          description: t('collection.title'),
          link: `/collections/${nftData.collection?.slug}`,
          name: nftData.collection.name || t('generic.noName'),
          avatar: nftData.collection.logo_image || placeholderImage
        };
      default:
        return null;
    }
  }, [useCurrentNft, nft, currentNftData, type]);

  const handleClick = useCallback(() => {
    if (!data) {
      return;
    }
    isImportBlockchainNft ? undefined : push(data.link);
  }, [data]);

  if (loading) {
    return <NftAvatarBlockSkeleton size={size} />;
  }

  if (!data) {
    return null;
  }

  return (
    <AvatarText
      text={hideDescription ? data.name : data.description}
      secondaryText={hideDescription ? undefined : data.name}
      avatar={data.avatar}
      shape="circle"
      size={size}
      onClick={!withLink ? undefined : handleClick}
      showAvatar={showAvatar}
      reverseText={reverseText}
      className={className}
      {...(data.verified && {
        badge: {
          placement: 'topLeft',
          icon: <CheckIcon title={t(`account.verified`)} />
        }
      })}
    />
  );
};
